import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import formatFloat from '../../../api/getChartSeries/formatFloat';
import runDateHistogramAggregationQuery from './runDateHistogramAggregationQuery';
import migrateRelativeDateRange from '../../../migrateRelativeDateRange';
import relativeDateRangeToDateRange from '../../../relativeDateRangeToDateRange';
import relativeDateRangeTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';

export const buildDateHistogramAggregationQuery = ({
  metrics,
  expression,
  filters,
  dateScope,
  dateInterval,
  groupBy,
  comparison,
}: {
  metrics: MetricInput[];
  expression?: Expression2;
  filters: FilterInput[];
  dateScope: DateRangeInput;
  dateInterval: FleetOps.Interval | FiscalInterval;
  groupByField: string;
  comparison: GqlComparison;
  groupBy: V5GroupBy;
}) => {
  let fiscalInterval: FleetOps.Interval | FiscalInterval | undefined =
    undefined;
  if (relativeDateRangeTypeCheckers.isFiscalInterval(dateInterval)) {
    fiscalInterval = dateInterval;
  }

  return {
    query: gql`
      query dateHistogramAggregation(
        $metrics: [MetricInput]!
        $expression: Expression2
        $filters: [FilterInput]!
        $dateScope: [DateRangeInput!]!
        $dateInterval: Interval
        $fiscalInterval: FiscalInterval
        $groupBy: GroupBy
        $comparison: Comparison
      ) {
        dateHistogramAggregation(
          metrics: $metrics
          expression: $expression
          filters: $filters
          dateScope: $dateScope
          groupBy: $groupBy
          dateInterval: $dateInterval
          fiscalInterval: $fiscalInterval
          comparison: $comparison
        )
      }
    `,
    variables: {
      metrics,
      filters: filters,
      dateScope,
      expression,
      dateInterval: fiscalInterval ? undefined : dateInterval,
      fiscalInterval,
      groupBy,
      comparison,
    },
  };
};

const toGqlComparison = (
  comparison: PersistedComparisonType,
  weekStartsOn: WeekStartsOn,
  filters: FilterInput[],
  dateScope: DateRangeInput,
): GqlComparison | undefined => {
  if (comparison.relativeDateOption) {
    const { startDate: dateScopeStartDate } = dateScope;
    const { startDate, endDate } = relativeDateRangeToDateRange({
      relativeDateRange: migrateRelativeDateRange(
        comparison.relativeDateOption,
      ),
      startOfWeek: weekStartsOn,
      comparedTo: dateScopeStartDate,
    });
    return {
      compareType: comparison.compareType,
      startDate,
      endDate,
    };
  } else {
    return undefined;
  }
};

const refineResponse = ({
  response,
  gadget,
  comparisonDateRange,
}: {
  response: SingleMetricDateMatrixResponseRaw;
  gadget: VisualisationDefinitions.SingleMetricDateMatrix;
  comparisonDateRange: { startDate: string; endDate: string };
}): SingleMetricDateMatrixResponse => {
  const { groupByField, metricId } = gadget;
  const buildPoint = (p: HistogramPointRaw): HistogramPoint => {
    const date = p['date'];
    const value = p[metricId];
    const comparison = p[`${metricId}_comparison`] as number | null;
    const comparisonDelta = p[`${metricId}_delta`] as number | null;
    const interval = p['interval'];
    const valueDateRange = {
      startDate: p['date'],
      endDate: p['endDate'],
    };
    const comparisonDeltaPercent = comparisonDelta
      ? formatFloat((comparisonDelta / value) * 100, 2)
      : null;

    return {
      date,
      value,
      valueDateRange,
      comparison,
      comparisonDelta,
      comparisonDeltaPercent,
      comparisonDateRange,
      interval,
    };
  };

  return {
    histogram: response.histogram ? response.histogram.map(buildPoint) : [],
    terms: response.terms
      ? response.terms.map((r) => {
          const groupTotal = r[metricId] as number;
          const group = r[groupByField] as string;
          return {
            group,
            groupTotal,
            histogram: r.histogram.map(buildPoint),
          };
        })
      : [],
    // We can find more terms than totalTerms in the case
    // where the terms includes a "Blank" term
    isFullSet: response.terms
      ? response.terms.length >= response.totalTerms
      : true,
    totalTerms: response.totalTerms,
  };
};

const buildGroupBy = (
  gadget: VisualisationDefinitions.SingleMetricDateMatrix,
  limitOverride?: number,
): V5GroupBy => {
  return {
    field: gadget.groupByField,
    type: 'text',
    limit: limitOverride ? limitOverride : gadget.limit,
    sortByMetric:
      gadget.serverSortBy === 'metric (asc)' ||
      gadget.serverSortBy === 'metric (desc)'
        ? gadget.metricId
        : undefined,
    sort:
      gadget.serverSortBy === 'metric (asc)' ||
      gadget.serverSortBy === 'alpha (asc)'
        ? 'asc'
        : 'desc',
  };
};

const dateHistogramAggregation = async ({
  metrics,
  expression,
  filterInput,
  dateScope,
  groupByField,
  dateInterval,
  comparison,
  gadget,
  weekStartsOn,
  limitOverride,
  client,
}: {
  metrics: MetricInput[];
  expression?: Expression2;
  filterInput: FilterInput[];
  dateScope: DateRangeInput;
  groupByField: string;
  dateInterval: FleetOps.Interval | FiscalInterval;
  comparison: PersistedComparisonType;
  gadget: VisualisationDefinitions.SingleMetricDateMatrix;
  weekStartsOn: WeekStartsOn;
  limitOverride?: number;
  client: ApolloClient<NormalizedCacheObject>;
}) => {
  const gqlComparison = toGqlComparison(
    comparison,
    weekStartsOn,
    filterInput,
    dateScope,
  );
  if (!gqlComparison || !gqlComparison.startDate || !gqlComparison.endDate) {
    return {
      histogram: [],
      terms: [],
      isFullSet: true,
      totalTerms: 0,
    };
  }

  const query = buildDateHistogramAggregationQuery({
    metrics,
    expression,
    filters: filterInput,
    dateScope,
    groupByField,
    dateInterval,
    groupBy: buildGroupBy(gadget, limitOverride),
    comparison: gqlComparison,
  });

  const response = await runDateHistogramAggregationQuery({ query, client });
  const rawResponse = response.data
    .dateHistogramAggregation as SingleMetricDateMatrixResponseRaw;

  return refineResponse({
    response: rawResponse,
    gadget,
    comparisonDateRange: {
      startDate: gqlComparison.startDate,
      endDate: gqlComparison.endDate,
    },
  });
};

export default dateHistogramAggregation;
